import React, { useState, useEffect } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { useAuth } from "../context/AuthContext";

const ALL_PERMS = [
  { key: "upload_invoice", label: "Upload Invoices",  icon: "⬆", desc: "Can upload & extract PDFs" },
  { key: "edit_invoice",   label: "Edit Invoices",    icon: "✏", desc: "Can edit invoice fields" },
  { key: "delete_invoice", label: "Delete Invoices",  icon: "🗑", desc: "Can delete invoice records" },
  { key: "export_csv",     label: "Export CSV / ZIP", icon: "⬇", desc: "Can export data" },
];

function PermissionsPanel({ user: u, onClose, onSaved }) {
  const [perms, setPerms] = useState(u.permissions || []);
  const [saving, setSaving] = useState(false);

  const toggle = (key) =>
    setPerms(prev => prev.includes(key) ? prev.filter(p => p !== key) : [...prev, key]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`/api/users/${u.id}/permissions`, { permissions: perms });
      toast.success(`Permissions updated for ${u.username}`);
      onSaved({ ...u, permissions: perms });
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.error || "Failed to save permissions");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" style={{ width: 480 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Permissions — <span style={{ color: "var(--accent)" }}>{u.username}</span></h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: 12, color: "var(--text3)", marginBottom: 16 }}>
            Select what this user is allowed to do.
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {ALL_PERMS.map(p => {
              const active = perms.includes(p.key);
              return (
                <label
                  key={p.key}
                  onClick={() => toggle(p.key)}
                  style={{
                    display: "flex", alignItems: "center", gap: 14, padding: "12px 16px",
                    borderRadius: 10, cursor: "pointer",
                    border: `1px solid ${active ? "var(--accent)" : "var(--border)"}`,
                    background: active ? "rgba(137,180,250,.08)" : "var(--surface2)",
                    transition: "all .15s",
                  }}
                >
                  <span style={{ fontSize: 20 }}>{p.icon}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, color: active ? "var(--accent)" : "var(--text)" }}>
                      {p.label}
                    </div>
                    <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 2 }}>{p.desc}</div>
                  </div>
                  <div style={{
                    width: 20, height: 20, borderRadius: 6,
                    border: `2px solid ${active ? "var(--accent)" : "var(--border)"}`,
                    background: active ? "var(--accent)" : "transparent",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    flexShrink: 0, transition: "all .15s",
                  }}>
                    {active && <span style={{ color: "#1e1e2e", fontSize: 13, fontWeight: 800 }}>✓</span>}
                  </div>
                </label>
              );
            })}
          </div>
          <div style={{ marginTop: 20, display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button className="btn-ghost" onClick={onClose}>Cancel</button>
            <button className="btn-primary" onClick={save} disabled={saving}>
              {saving ? "Saving…" : "Save Permissions"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function UsersPage() {
  const { user }  = useAuth();
  const [users, setUsers]         = useState([]);
  const [form,  setForm]          = useState({ username: "", password: "", role: "user" });
  const [loading, setLoading]     = useState(false);
  const [permUser, setPermUser]   = useState(null);

  const load = async () => {
    try {
      const res = await axios.get("/api/users");
      setUsers(res.data);
    } catch (e) {
      toast.error("Failed to load users");
    }
  };

  useEffect(() => { load(); }, []);

  const addUser = async () => {
    if (!form.username || !form.password) { toast.error("Fill all fields"); return; }
    setLoading(true);
    try {
      await axios.post("/api/users", form);
      toast.success(`User "${form.username}" created`);
      setForm({ username: "", password: "", role: "user" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || "Failed");
    } finally { setLoading(false); }
  };

  const toggleActive = async (u) => {
    try {
      await axios.put(`/api/users/${u.id}/toggle`, { is_active: !u.is_active });
      toast.success(`User ${u.is_active ? "deactivated" : "activated"}`);
      load();
    } catch { toast.error("Failed"); }
  };

  if (user?.role !== "admin") {
    return <div className="empty-state"><p>Admin access required.</p></div>;
  }

  return (
    <div className="users-page">
      {permUser && (
        <PermissionsPanel
          user={permUser}
          onClose={() => setPermUser(null)}
          onSaved={updated => {
            setUsers(prev => prev.map(u => u.id === updated.id ? updated : u));
            setPermUser(null);
          }}
        />
      )}

      <div className="users-add-card">
        <h3>Add New User</h3>
        <div className="users-form">
          <input
            placeholder="Username"
            value={form.username}
            onChange={e => setForm(p => ({ ...p, username: e.target.value }))}
          />
          <input
            type="password"
            placeholder="Password"
            value={form.password}
            onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
          />
          <select value={form.role} onChange={e => setForm(p => ({ ...p, role: e.target.value }))}>
            <option value="user">User</option>
            <option value="admin">Admin</option>
          </select>
          <button className="btn-primary" onClick={addUser} disabled={loading}>
            {loading ? "Creating…" : "Create User"}
          </button>
        </div>
      </div>

      <div className="users-table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th>Username</th><th>Role</th>
              <th>Status</th><th>Permissions</th><th>Created</th><th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 && (
              <tr><td colSpan={7} style={{ textAlign: "center", color: "var(--text3)", padding: 24 }}>No users yet</td></tr>
            )}
            {users.map(u => (
              <tr key={u.id}>
                <td>{u.username}</td>
                <td><span className={`role-badge role-${u.role}`}>{u.role}</span></td>
                <td>
                  <span className={`status-badge ${u.is_active ? "status-ok" : "status-off"}`}>
                    {u.is_active ? "Active" : "Inactive"}
                  </span>
                </td>
                <td>
                  {u.role === "admin" ? (
                    <span style={{ fontSize: 11, color: "var(--text3)" }}>All</span>
                  ) : (
                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                      {(u.permissions || []).length === 0 && (
                        <span style={{ fontSize: 11, color: "var(--err)", opacity: .7 }}>No permissions</span>
                      )}
                      {(u.permissions || []).map(p => {
                        const meta = ALL_PERMS.find(x => x.key === p);
                        return (
                          <span key={p} style={{
                            fontSize: 10, padding: "2px 7px", borderRadius: 5,
                            background: "rgba(137,180,250,.12)", color: "var(--accent)",
                            border: "1px solid rgba(137,180,250,.25)", whiteSpace: "nowrap",
                          }}>
                            {meta?.icon} {meta?.label ?? p}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </td>
                <td>{u.created_at?.slice(0, 10)}</td>
                <td style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {u.role !== "admin" && (
                    <button
                      className="btn-tiny"
                      style={{ color: "var(--accent)", borderColor: "rgba(137,180,250,.3)" }}
                      onClick={() => setPermUser(u)}
                    >
                      🔑 Permissions
                    </button>
                  )}
                  <button
                    className="btn-tiny"
                    onClick={() => toggleActive(u)}
                    disabled={u.username === user.username}
                  >
                    {u.is_active ? "Deactivate" : "Activate"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}